import { inject, Injectable } from '@angular/core';
import { Deferred, isDefined } from '@sama/js-core';
import { mapKeys, values } from '@sama/micro-dash';
import { isNil, mean } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { TrackingEvent } from '../../../cross-team-shared/event-tracking/event-utils';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import {
  isOrthographicVideoOutput,
  isPointCloudOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import {
  getOrThrow,
  strictValues,
} from '../../../cross-team-shared/misc-utils/strict-record';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { ContentConnector } from '../../../team-apis/content/content-connector';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { RedirectResponse } from '../../../team-apis/otterdam/redirect-response';
import {
  Task,
  TaskExternal,
  TrainingTaskType,
} from '../../../team-apis/otterdam/task';
import { getOutputById } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { flipShapeYCoordinates } from '../../outputs/annotation/annotation-shared/fusion/projection/orthographic-utils';
import { PersistenceManager } from '../../production-workspace/submissions/persistence-manager';
import {
  TaskSubmissionBenignError,
  TaskSubmissionError,
} from '../../production-workspace/submissions/submit-task.service';
import { isRedirectResponse } from '../navigation/is-redirect-response';
import { TrainingTaskResourceService } from '../resources/training-task-resource.service';
import {
  isSubmissionErrors,
  SubmissionErrors,
} from '../utils/submission-errors-utils';
import { TrainingTaskServiceObjectless } from './training-task-objectless.service';

export const SHOW_HINTS_DURATION = 7000;
@Injectable()
export class TrainingTaskService {
  private eventTrackingService = inject(EventTrackingService);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private messagingService = inject(MessagingService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private trainingTaskResource = inject(TrainingTaskResourceService);
  private trainingTaskServiceObjectless = inject(TrainingTaskServiceObjectless);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private persistenceManager = inject(PersistenceManager);
  private workspaceLoadingService = inject(WorkspaceLoadingService);

  loadLatestScore(task: Task) {
    if (!this.shouldLoadLatestScore(task)) {
      return;
    }

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return this.trainingTaskServiceObjectless.calculateAndStoreScoreObjectless(
        task.answers_or_gold,
      );
    }

    return this.calculateAndStoreScore();
  }

  async calculateScore(event: TrackingEvent & { source?: string }) {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return this.trainingTaskServiceObjectless.calculateScoreObjectless(event);
    }

    if (this.taskWorkspaceStore.state().task.round <= 1) {
      const isTaskSubmissionSuccessful = await this.handleTaskSubmission();
      if (!isTaskSubmissionSuccessful) {
        return;
      }
    }

    await this.calculateAndStoreScore();

    this.eventTrackingService.track(event);
  }

  hasPassingScore$() {
    return this.taskWorkspaceStore('outputTrainingScore').$.pipe(
      map((scores) => {
        const meanScoreInPercentile =
          mean(values(scores).filter(isDefined)) * 100;

        const passMark =
          this.taskWorkspaceStore.state().task._extras.training?.pass_mark;
        return !isNil(passMark) ? meanScoreInPercentile >= passMark : undefined;
      }),
    );
  }

  isInPracticeMode() {
    const taskExtras = this.taskWorkspaceStore.state().task._extras;
    return taskExtras.training?.type === TrainingTaskType.PRACTICE;
  }

  hasScoreCalculated$() {
    return this.taskWorkspaceStore('outputTrainingScore').$.pipe(
      map((scores) => values(scores).some(isDefined)),
    );
  }

  meanScore$() {
    return this.taskWorkspaceStore('outputTrainingScore').$.pipe(
      map((scores) => mean(values(scores).filter(isDefined))),
    );
  }

  showHints(event: TrackingEvent & { source: string }) {
    const contentConnectors: ContentConnector[] = [];

    this.fusionManagerFactory.getManagers().forEach((fusionManager) => {
      fusionManager.minions.forEach((minion) => {
        contentConnectors.push(minion.contentConnector);
        this.showContentShapes(minion.output, minion.contentConnector);
      });
    });

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.trainingTaskServiceObjectless.showNonAnnotationHints();
    }

    const completionDeferred = new Deferred<void>();
    this.eventTrackingService.track(event);
    setTimeout(() => {
      this.hideHints(contentConnectors);
      completionDeferred.resolve();
    }, SHOW_HINTS_DURATION);

    return completionDeferred.promise;
  }

  private async calculateAndStoreScore() {
    const { score, shape_feedback } = await lastValueFrom(
      this.trainingTaskResource.calculateScore$(),
    );

    shape_feedback.forEach((feedback) => {
      feedback.feedbackId = uuidV4();
    });

    this.taskWorkspaceStore.batch(() => {
      const { project } = this.taskWorkspaceStore.state();

      this.taskWorkspaceStore('outputTrainingScore').set(
        mapKeys(score, (_, key) => getOutputById(project, +key).name),
      );
      this.taskWorkspaceStore('feedbackType').set('shape');
      this.taskWorkspaceStore('shapeFeedback').set(shape_feedback);
    });
  }

  private async handleTaskSubmission() {
    try {
      await this.submitTaskAndIncrementRoundLocally();
    } catch (error) {
      if (error instanceof TaskSubmissionError) {
        this.messagingService.error(error.message);
        return false;
      } else if (error instanceof TaskSubmissionBenignError) {
        return false;
      }

      throw error;
    }

    return true;
  }

  private async submitTaskAndIncrementRoundLocally() {
    this.workspaceLoadingService.isLoading$.next(true);

    try {
      const result = await lastValueFrom(
        this.persistenceManager.submit(
          this.workspaceConfigService.qaGroupId ?? '',
        ),
      );

      this.verifySubmitResult(result);

      this.taskWorkspaceStore('task')('round').set(result.round);

      this.workspaceLoadingService.isLoading$.next(false);
    } catch (e) {
      if (!(e instanceof TaskSubmissionError)) {
        this.workspaceLoadingService.isLoading$.next(false);
      }
      throw e;
    }
  }

  private verifySubmitResult(
    result: TaskExternal | RedirectResponse | SubmissionErrors,
  ): asserts result is Task {
    if (isSubmissionErrors(result)) {
      if (result.serverErrors.length) {
        throw new TaskSubmissionError(
          'Error while submitting a task. Please reload the page.',
        );
      }

      throw new TaskSubmissionBenignError();
    }

    if (isRedirectResponse(result)) {
      throw new Error('Got redirect response, but expected a task.', {
        cause: `redirect_to: ${result.redirect_to}, message: ${result.message}`,
      });
    }

    if (result.id !== this.taskWorkspaceStore.state().task.id) {
      throw new Error('Expected to receive the same task with round 2.');
    }
  }

  private showContentShapes(
    output: AnnotationOutput,
    contentConnector: ContentConnector,
  ) {
    const { outputAnnotationInfo, project } = this.taskWorkspaceStore.state();
    let trainingShapes = strictValues(
      getOrThrow(outputAnnotationInfo, output.name).trainingShapes ?? {},
    );

    const orthographicOutput = project.outputs.find(isOrthographicVideoOutput);
    if (orthographicOutput && isPointCloudOutput(output)) {
      trainingShapes = this.appendOrthographicShapes(
        trainingShapes,
        orthographicOutput.name,
      );
    }
    if (isOrthographicVideoOutput(output)) {
      trainingShapes = this.formatOrthographicShapes(trainingShapes);
    }

    contentConnector.showTrainingShapes(trainingShapes);
  }

  private hideHints(contentConnectors: ContentConnector[]) {
    contentConnectors.forEach((contentConnector) => {
      contentConnector.showTrainingShapes([]);
    });

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.trainingTaskServiceObjectless.hideNonAnnotationHints();
    }
  }

  private shouldLoadLatestScore(task: Task) {
    return task.round > 1 && this.isInPracticeMode();
  }

  private appendOrthographicShapes(
    trainingShapes: ShapeState[],
    orthographicOutputName: string,
  ) {
    const { outputAnnotationInfo } = this.taskWorkspaceStore.state();

    return [
      ...trainingShapes,
      ...strictValues(
        outputAnnotationInfo[orthographicOutputName]?.trainingShapes ?? {},
      ),
    ];
  }

  private formatOrthographicShapes(trainingShapes: ShapeState[]) {
    return trainingShapes.map(flipShapeYCoordinates);
  }
}
